// P141R17: real Google Maps JavaScript API loader - a plain, dependency-free
// script-tag loader (no @googlemaps/js-api-loader package added) shared by
// every real map surface in this app (War Room mini map, War Room inline
// Footstep panel, Footstep History's own map). Caches the load per real API
// key so multiple map components on the same page share one script tag and
// one `google.maps` namespace, matching how the real Google Maps JS API
// itself expects to be loaded (loading it twice with different callback
// names throws a real console warning from Google's own code).

let cachedKey = null;
let cachedPromise = null;

/**
 * Loads the real Google Maps JavaScript API for the given real key and
 * resolves with `window.google.maps`. Never called with an empty/missing
 * key by any caller in this app - every map component checks for a real
 * key first and renders its own honest fallback (keyless embed / blocked
 * state) instead of calling this when none is configured.
 */
export function loadGoogleMaps(apiKey) {
  // If a real `google.maps` namespace already exists on window - whether
  // this loader put it there or (in a test) it was stubbed directly -
  // reuse it immediately. Real Google Maps JS itself does not support
  // loading a second copy with a different key in the same page anyway, so
  // there is no real scenario where "already loaded, but for a different
  // key" needs separate handling here.
  if (typeof window !== "undefined" && window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (cachedPromise && cachedKey === apiKey) {
    return cachedPromise;
  }

  cachedKey = apiKey;
  cachedPromise = new Promise((resolve, reject) => {
    const callbackName = `__wxGoogleMapsLoaded_${Date.now()}`;
    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google.maps);
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=visualization&callback=${callbackName}`;
    script.async = true;
    script.onerror = () => {
      delete window[callbackName];
      cachedPromise = null;
      reject(new Error("Failed to load the Google Maps JavaScript API"));
    };
    document.head.appendChild(script);
  });

  return cachedPromise;
}

/** Test-only reset so each test starts with a clean loader cache. */
export function resetGoogleMapsLoaderForTests() {
  cachedKey = null;
  cachedPromise = null;
}
